import { useState } from "react";
import {
    Box,
    Typography,
    Button,
    Stack,
    CircularProgress,
    TextField,
    Dialog,
    MenuItem,
    Select,
    type SelectChangeEvent,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { FoodsIcon } from "@foodpilot/foods";
import { useSnackbar } from "src/utils/useSnackbar";
import { useCreatePermission, PermissionPayload } from "src/api/client-api/permissions/useCreatePermission";
import { Permission } from "src/api/client-api/permissions/useGetAllPermissions";
import { getAllObjects, getAllSlugs } from "src/context/FoodpilotContext";

type PermissionModalProps = {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    editingPermission?: Permission | null;
};

export const PermissionModal = (props: PermissionModalProps) => {
    const { open, onClose, onSuccess, editingPermission } = props;
    const { t } = useTranslation();
    const { setSnackbarProps } = useSnackbar();

    const isEditing = !!editingPermission;

    const [identifier, setIdentifier] = useState(() => editingPermission?.identifier || "");
    const [slug, setSlug] = useState(() => editingPermission?.slug || "");
    const [object, setObject] = useState(() => editingPermission?.object || "");
    const [extra, setExtra] = useState(() => editingPermission?.extra || "");

    const createPermissionMutation = useCreatePermission();
    const isCreating = createPermissionMutation.isLoading;

    const handleSave = async () => {
        // Validate required fields
        if (!identifier.trim()) {
            setSnackbarProps({
                message: t("permission.identifier_required", "Identifier is required"),
                type: "error",
            });
            return;
        }

        if (!slug.trim()) {
            setSnackbarProps({
                message: t("permission.slug_required", "Action is required"),
                type: "error",
            });
            return;
        }

        if (!object.trim()) {
            setSnackbarProps({
                message: t("permission.object_required", "Object is required"),
                type: "error",
            });
            return;
        }

        try {
            const permissionPayload: PermissionPayload = {
                id: editingPermission?.id,
                identifier: identifier.trim(),
                slug: slug.trim(),
                object: object.trim(),
                extra: extra.trim() || null,
            };

            await createPermissionMutation.mutateAsync({
                isNew: !isEditing,
                permission: permissionPayload,
            });

            setSnackbarProps({
                message:
                    isEditing ?
                        t("permission.update_success", "Permission updated successfully")
                    :   t("permission.create_success", "Permission created successfully"),
                type: "success",
            });

            onSuccess();
            onClose();
        } catch (error) {
            setSnackbarProps({
                message:
                    isEditing ?
                        t("permission.update_error", "Failed to update permission")
                    :   t("permission.create_error", "Failed to create permission"),
                type: "error",
            });
        }
    };

    const isFormValid = identifier.trim().length > 0 && slug.trim().length > 0 && object.trim().length > 0;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: "16px",
                    overflow: "hidden",
                },
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "32px",
                    borderBottom: "1px solid #E7E6E4",
                    backgroundColor: "white",
                }}
            >
                <Box>
                    <Typography variant="h3" sx={{ lineHeight: "1.6em", color: "#1E1E1E", marginBottom: "8px" }}>
                        {isEditing ?
                            t("permission.edit_title", "Edit Permission")
                        :   t("permission.create_title", "Create Permission")}
                    </Typography>
                    <Typography variant="small-medium" sx={{ lineHeight: "1.6em", color: "#50504D" }}>
                        {isEditing ?
                            t("permission.edit_description", "Update the parameters for your permission.")
                        :   t("permission.create_description", "Set the parameters for your permission.")}
                    </Typography>
                </Box>
                <Box
                    onClick={onClose}
                    sx={{
                        width: "40px",
                        height: "40px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        borderRadius: "50%",
                        "&:hover": {
                            backgroundColor: "#F5F5F5",
                        },
                    }}
                >
                    <FoodsIcon icon="close" size={1} />
                </Box>
            </Box>

            {/* Content */}
            <Box
                sx={{
                    padding: "32px",
                    backgroundColor: "#FEFCFB",
                }}
            >
                <Stack spacing={3}>
                    {/* Identifier Field */}
                    <Box>
                        <Typography variant="h6" sx={{ lineHeight: "1.6em", color: "#393A36", marginBottom: "8px" }}>
                            {t("permission.identifier", "Identifier")}:
                        </Typography>
                        <TextField
                            fullWidth
                            value={identifier}
                            onChange={(e) => setIdentifier(e.target.value)}
                            placeholder={t("permission.identifier_placeholder", "Enter permission identifier")}
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: "24px",
                                    height: "40px",
                                    backgroundColor: "white",
                                    "& fieldset": {
                                        borderColor: "#E7E6E4",
                                    },
                                    "&:hover fieldset": {
                                        borderColor: "#D0D0D0",
                                    },
                                    "&.Mui-focused fieldset": {
                                        borderColor: (theme) => theme.palette.primary[600],
                                    },
                                },
                                "& .MuiOutlinedInput-input": {
                                    fontSize: "0.875rem",
                                    padding: "0px 16px",
                                    color: "#393A36",
                                    "&::placeholder": {
                                        color: "#A3A2A0",
                                        opacity: 1,
                                    },
                                },
                            }}
                        />
                    </Box>

                    {/* Object Field */}
                    <Box>
                        <Typography variant="h6" sx={{ lineHeight: "1.6em", color: "#393A36", marginBottom: "8px" }}>
                            {t("permission.object", "Object")}:
                        </Typography>
                        <Select
                            fullWidth
                            value={object}
                            onChange={(e: SelectChangeEvent) => setObject(e.target.value)}
                            displayEmpty
                            renderValue={(selected) =>
                                selected ? selected : (
                                    <span style={{ color: "#A3A2A0" }}>
                                        {t("permission.object_placeholder", "Select object/resource type")}
                                    </span>
                                )
                            }
                            sx={{
                                borderRadius: "24px",
                                height: "40px",
                                backgroundColor: "white",
                                "& fieldset": {
                                    borderColor: "#E7E6E4",
                                },
                                "&:hover fieldset": {
                                    borderColor: "#D0D0D0",
                                },
                                "&.Mui-focused fieldset": {
                                    borderColor: (theme) => theme.palette.primary[600],
                                },
                                "& .MuiSelect-select": {
                                    fontSize: "0.875rem",
                                    padding: "0px 16px",
                                    color: "#393A36",
                                },
                            }}
                        >
                            {getAllObjects().map((entity: string) => (
                                <MenuItem key={entity} value={entity}>
                                    {entity}
                                </MenuItem>
                            ))}
                        </Select>
                    </Box>

                    {/* Slug Field */}
                    <Box>
                        <Typography variant="h6" sx={{ lineHeight: "1.6em", color: "#393A36", marginBottom: "8px" }}>
                            {t("permission.slug", "Action")}:
                        </Typography>
                        <Select
                            fullWidth
                            value={slug}
                            onChange={(e: SelectChangeEvent) => setSlug(e.target.value)}
                            displayEmpty
                            renderValue={(selected) =>
                                selected ? selected : (
                                    <span style={{ color: "#A3A2A0" }}>
                                        {t("permission.slug_placeholder", "Select action")}
                                    </span>
                                )
                            }
                            sx={{
                                borderRadius: "24px",
                                height: "40px",
                                backgroundColor: "white",
                                "& fieldset": {
                                    borderColor: "#E7E6E4",
                                },
                                "&:hover fieldset": {
                                    borderColor: "#D0D0D0",
                                },
                                "&.Mui-focused fieldset": {
                                    borderColor: (theme) => theme.palette.primary[600],
                                },
                                "& .MuiSelect-select": {
                                    fontSize: "0.875rem",
                                    padding: "0px 16px",
                                    color: "#393A36",
                                },
                            }}
                        >
                            {getAllSlugs().map((slugOption: string) => (
                                <MenuItem key={slugOption} value={slugOption}>
                                    {slugOption}
                                </MenuItem>
                            ))}
                        </Select>
                    </Box>

                    {/* Extra Field */}
                    <Box>
                        <Typography variant="h6" sx={{ lineHeight: "1.6em", color: "#393A36", marginBottom: "8px" }}>
                            {t("permission.extra", "Extra expression")}:
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            value={extra}
                            onChange={(e) => setExtra(e.target.value)}
                            placeholder={t("permission.extra_placeholder", "Enter extra expression (optional)")}
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: "12px",
                                    backgroundColor: "white",
                                    "& fieldset": {
                                        borderColor: "#E7E6E4",
                                    },
                                    "&:hover fieldset": {
                                        borderColor: "#D0D0D0",
                                    },
                                    "&.Mui-focused fieldset": {
                                        borderColor: (theme) => theme.palette.primary[600],
                                    },
                                },
                                "& .MuiOutlinedInput-input": {
                                    fontSize: "0.875rem",
                                    padding: "12px 16px",
                                    color: "#393A36",
                                    "&::placeholder": {
                                        color: "#A3A2A0",
                                        opacity: 1,
                                    },
                                },
                            }}
                        />
                    </Box>
                </Stack>
            </Box>

            {/* Footer */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    padding: "0px 24px 0px 32px",
                    height: "72px",
                    backgroundColor: "white",
                    borderTop: "1px solid #E7E6E4",
                }}
            >
                <Button
                    onClick={handleSave}
                    disabled={!isFormValid || isCreating}
                    sx={(theme) => ({
                        width: "150px",
                        height: "40px",
                        borderRadius: "44px",
                        textTransform: "none",
                        fontWeight: 700,
                        fontSize: "0.875rem",
                        lineHeight: "1.5em",
                        background: `linear-gradient(180deg, ${theme.palette.primary[500]} 0%, ${theme.palette.primary[600]} 100%)`,
                        color: "white !important",
                        opacity: isFormValid ? 1 : 0.36,
                        boxShadow: "0px 1px 2px 0px rgba(42, 43, 39, 0.02)",
                        "&:hover": {
                            background: `linear-gradient(180deg, ${theme.palette.primary[600]} 0%, ${theme.palette.primary[700]} 100%)`,
                            boxShadow: "0px 1px 3px 0px rgba(42, 43, 39, 0.03)",
                        },
                        "&:disabled": {
                            background: `linear-gradient(180deg, ${theme.palette.primary[500]} 0%, ${theme.palette.primary[600]} 100%)`,
                            color: "white !important",
                        },
                    })}
                >
                    {isCreating ?
                        <CircularProgress size={20} color="inherit" />
                    : isEditing ?
                        t("permission.update", "Update")
                    :   t("permission.save", "Save")}
                </Button>
            </Box>
        </Dialog>
    );
};
